import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import usePortal from 'react-useportal';
import FallbackContentAnimation from '../../../../components/FallbackContentAnimation';
import { TransactionsPreloader } from '../../../../components/ContentPreloaders';
import { ContractOfferPopup } from '../../../../components/Popup';
import { getPlayerInfo } from '../../../../api/league';
import { teamGetMyTeam } from '../../../../redux/actions/Team';
import { showPopupWindow } from '../../../../redux/actions/Popups';
import {
  getAcademyList,
  getMyAllSquad,
  myTeamLoading,
} from '../../../../redux/selector/Team';
import { getPopupByName } from '../../../../redux/selector/Popups';
import { getUser } from '../../../../redux/selector/User';
import { getLoadingUniversesData } from '../../../../redux/selector/SignUp';
import { homeData } from '../../../../redux/selector/Home';
import { getUniverses } from '../../../../redux/actions/SignUp';
import { digitFormater } from '../../../../service/functions';
import { POPUPS, ratingRangeStatus } from '../../../../service/constants';
import { contractMatchesThisSeason } from '../../../../service/contractPlaytime';
import {
  leagueDivisionFromName,
  leagueNameFromUniverses,
  recommendedDailySalaryForLeague,
} from '../../../../service/leagueTable';
import {
  StyledPage,
  StyledColumns,
  StyledSection,
  StyledStatGrid,
  StyledStat,
  StyledStatIcon,
  StyledStatCopy,
  StyledStatLabel,
  StyledStatValue,
  StyledStatHint,
  StyledSquadLink,
  StyledList,
  StyledListHead,
  StyledListRow,
  StyledPlayerName,
  StyledMeta,
  StyledWage,
  StyledPlaytime,
  StyledStatus,
  StyledExtendButton,
  StyledEmpty,
} from './styled';
import { StyledTransactionPreloaderContainer } from '../../styled';
import AutoContractsPanel from './AutoContractsPanel';

const WAGE_COLUMNS = 'minmax(0, 1fr) 108px 72px';
const EXPIRING_COLUMNS = 'minmax(0, 1fr) 88px';
const PLAYTIME_COLUMNS = 'minmax(0, 1fr) 88px';

const SHORT_RANGE = {
  0: 'Weakest',
  1: 'Weak',
  2: 'Avg',
  3: 'Strong',
  4: 'WC',
  5: 'Legend',
};

const strokeIcon = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '2',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const DollarIcon = () => (
  <svg {...strokeIcon}>
    <line x1="12" x2="12" y1="2" y2="22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const CapIcon = () => (
  <svg {...strokeIcon}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const DeltaIcon = () => (
  <svg {...strokeIcon}>
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);

const CalendarIcon = () => (
  <svg {...strokeIcon}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" x2="16" y1="2" y2="6" />
    <line x1="8" x2="8" y1="2" y2="6" />
    <line x1="3" x2="21" y1="10" y2="10" />
  </svg>
);

const GoalIcon = () => (
  <svg {...strokeIcon}>
    <path d="M8 21h8" />
    <path d="M12 17v4" />
    <path d="M7 4h10l1 5-6 3-6-3 1-5z" />
    <path d="M4 9v4a8 8 0 0 0 16 0V9" />
  </svg>
);

import {
  finiteNumber,
} from '../../../../service/contractPlaytime';

const formatPlayerName = (player) => {
  const nick = player.nick_name;
  const first = player.first_name || '';
  const last = player.last_name || '';
  if (nick) {
    return `${first} "${nick}" ${last}`.replace(/\s+/g, ' ').trim();
  }
  return `${first} ${last}`.trim() || '—';
};

const shortSkillPot = (player) => {
  const skill =
    SHORT_RANGE[player.rating_range] ||
    ratingRangeStatus[player.rating_range];
  const pot =
    SHORT_RANGE[player.potential_range] ||
    ratingRangeStatus[player.potential_range];
  if (!skill && !pot) return '—';
  return `${skill || '—'} / ${pot || '—'}`;
};

const contractGoal = (player) => {
  const contract = player.contract || {};
  const playTime = finiteNumber(
    contract.data?.play_time ?? contract.play_time ?? player.play_time
  );
  const played = finiteNumber(
    contractMatchesThisSeason(contract) ??
      contract.played_games ??
      player.played_games
  );
  const met = contract.met_contract_play_terms ?? player.met_contract_play_terms;

  if (playTime != null && playTime > 0 && played != null) {
    return { hasData: true, unmet: played < playTime, playTime, played };
  }
  if (playTime != null && playTime > 0) {
    return { hasData: true, unmet: true, playTime, played: null };
  }
  if (Array.isArray(met)) {
    const scored = met.filter((value) => value === 0 || value === 1);
    if (!scored.length) return { hasData: false, unmet: false };
    return {
      hasData: true,
      unmet: scored[scored.length - 1] === 0,
      playTime,
      played,
    };
  }
  if (met === false) return { hasData: true, unmet: true, playTime, played };
  if (met === true) return { hasData: true, unmet: false, playTime, played };
  return { hasData: false, unmet: false };
};

const withContract = (player, contractsById) => ({
  ...player,
  contract: contractsById[player._id] ?? player.contract,
});

export default function Contracts() {
  const dispatch = useDispatch();
  const squad = useSelector(getMyAllSquad) || [];
  const academyIds = useSelector(getAcademyList) || [];
  const isTeamLoading = useSelector(myTeamLoading);
  const { leagueName: leagueCode } = useSelector(getUser);
  const home = useSelector(homeData);
  const { universesList } = useSelector(getLoadingUniversesData);
  const contractExtendPopupData = useSelector(
    getPopupByName(POPUPS.CONTRACT_EXTEND_POPUP)
  );

  const [contractsById, setContractsById] = useState({});
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsTick, setContractsTick] = useState(0);
  const extendPopupWasOpen = useRef(false);
  const lastExtendId = useRef(null);

  const { Portal } = usePortal({
    bindTo: typeof document !== 'undefined' && document.querySelector('#modal'),
  });

  useEffect(() => {
    dispatch(teamGetMyTeam());
  }, [dispatch]);

  useEffect(() => {
    if (!universesList || universesList.length === 0) {
      dispatch(getUniverses(false));
    }
  }, [dispatch, universesList]);

  const academySet = useMemo(() => new Set(academyIds), [academyIds]);

  const roster = useMemo(
    () =>
      squad.filter(
        (player) => !player.loanedOut && !academySet.has(player._id)
      ),
    [squad, academySet]
  );

  const rosterIds = useMemo(
    () =>
      squad
        .map((player) => player._id)
        .filter(Boolean)
        .sort()
        .join(','),
    [squad]
  );

  useEffect(() => {
    if (!rosterIds) {
      setContractsById({});
      setContractsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const ids = rosterIds.split(',');
    setContractsLoading(true);

    (async () => {
      const pairs = await Promise.all(
        ids.map(async (id) => {
          try {
            const response = await getPlayerInfo(id);
            if (response.status !== 200) return [id, null];
            const payload = await response.json();
            return [id, payload.contract ?? null];
          } catch {
            return [id, null];
          }
        })
      );
      if (cancelled) return;
      setContractsById(Object.fromEntries(pairs));
      setContractsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [rosterIds, contractsTick]);

  useEffect(() => {
    if (contractExtendPopupData?.id) {
      lastExtendId.current = contractExtendPopupData.id;
    }
    const open = Boolean(contractExtendPopupData);
    if (extendPopupWasOpen.current && !open && lastExtendId.current) {
      const id = lastExtendId.current;
      (async () => {
        try {
          const response = await getPlayerInfo(id);
          if (response.status === 200) {
            const payload = await response.json();
            setContractsById((prev) => ({
              ...prev,
              [id]: payload.contract ?? null,
            }));
          }
        } catch {
          /* keep previous contract */
        }
      })();
      dispatch(teamGetMyTeam());
    }
    extendPopupWasOpen.current = open;
  }, [contractExtendPopupData, dispatch]);

  const leagueLabel =
    home?.leagueName || leagueNameFromUniverses(leagueCode, universesList);
  const division = leagueDivisionFromName(leagueLabel || leagueCode);

  const wagePlayers = useMemo(
    () =>
      [...roster]
        .sort((a, b) => (Number(b.salary) || 0) - (Number(a.salary) || 0))
        .map((player) => ({
          ...player,
          displayName: formatPlayerName(player),
          skillPot: shortSkillPot(player),
          dailyWage: (Number(player.salary) || 0) / 7,
        })),
    [roster]
  );

  const expiringPlayers = useMemo(
    () =>
      squad
        .filter((player) => {
          const loanedIn = !isEmpty(player.lender_team) && !player.loanedOut;
          if (loanedIn) return false;
          return Number(player.contract_remaining_length) === 1;
        })
        .map((player) => withContract(player, contractsById))
        .map((player) => ({
          ...player,
          displayName: formatPlayerName(player),
          inAcademy:
            academySet.has(player._id) || player.academy?.status === 1,
          isExtended: !isEmpty(player.contract?.extension),
        })),
    [squad, academySet, contractsById]
  );

  const playtimePlayers = useMemo(
    () =>
      roster
        .map((player) => withContract(player, contractsById))
        .map((player) => ({
          ...player,
          displayName: formatPlayerName(player),
          goal: contractGoal(player),
        }))
        .filter((player) => player.goal.hasData)
        .sort((a, b) => Number(b.goal.unmet) - Number(a.goal.unmet)),
    [roster, contractsById]
  );

  const dailyWages = useMemo(
    () =>
      roster.reduce(
        (sum, player) => sum + (Number(player.salary) || 0) / 7,
        0
      ),
    [roster]
  );

  const expiringCount = expiringPlayers.length;
  const goalTracked = playtimePlayers.length;
  const unmetGoalCount = playtimePlayers.filter(
    (player) => player.goal.unmet
  ).length;

  const recommendedDaily = useMemo(
    () =>
      recommendedDailySalaryForLeague(
        home?.leagueName,
        leagueLabel,
        leagueCode
      ),
    [home, leagueLabel, leagueCode]
  );
  const vsRecommended =
    recommendedDaily != null ? dailyWages - recommendedDaily : null;
  const wageTone =
    vsRecommended > 0 ? 'danger' : vsRecommended < 0 ? 'success' : undefined;

  const extendPlayer = useMemo(() => {
    if (!contractExtendPopupData?.id) return null;
    return (
      squad.find((player) => player._id === contractExtendPopupData.id) || null
    );
  }, [contractExtendPopupData, squad]);

  const openExtend = (playerId) => {
    dispatch(showPopupWindow(playerId, POPUPS.CONTRACT_EXTEND_POPUP, false));
  };

  return (
    <FallbackContentAnimation
      loading={isTeamLoading}
      fallback={
        <StyledTransactionPreloaderContainer>
          <TransactionsPreloader style={{ width: '100%' }} />
        </StyledTransactionPreloaderContainer>
      }
    >
      <StyledPage>
      {extendPlayer && (
        <Portal>
          <ContractOfferPopup
            isExtended
            popupName={POPUPS.CONTRACT_EXTEND_POPUP}
            id={extendPlayer._id}
            salary={extendPlayer.salary}
          />
        </Portal>
      )}

      <AutoContractsPanel
        onContractsRefresh={() => {
          setContractsTick((value) => value + 1);
          dispatch(teamGetMyTeam());
        }}
      />

      <StyledColumns>
      <StyledSection>
        <h3>Wage bill</h3>
        <StyledStatGrid>
          <StyledStat>
            <StyledStatIcon $tone={wageTone}>
              <DollarIcon />
            </StyledStatIcon>
            <StyledStatCopy>
              <StyledStatLabel>Daily salary</StyledStatLabel>
              <StyledStatValue $tone={wageTone}>
                ${digitFormater(dailyWages, false, 1)}
              </StyledStatValue>
            </StyledStatCopy>
          </StyledStat>
          <StyledStat>
            <StyledStatIcon>
              <CapIcon />
            </StyledStatIcon>
            <StyledStatCopy>
              <StyledStatLabel>Recommended</StyledStatLabel>
              <StyledStatValue>
                {recommendedDaily != null
                  ? `$${digitFormater(recommendedDaily, false, 1)}`
                  : '—'}
              </StyledStatValue>
              {division ? (
                <StyledStatHint>League {division} cap</StyledStatHint>
              ) : null}
            </StyledStatCopy>
          </StyledStat>
          <StyledStat>
            <StyledStatIcon $tone={wageTone}>
              <DeltaIcon />
            </StyledStatIcon>
            <StyledStatCopy>
              <StyledStatLabel>
                {vsRecommended > 0
                  ? 'Over recommended'
                  : vsRecommended < 0
                    ? 'Under recommended'
                    : 'Vs recommended'}
              </StyledStatLabel>
              <StyledStatValue $tone={wageTone}>
                {vsRecommended == null
                  ? '—'
                  : vsRecommended === 0
                    ? '$0'
                    : `${vsRecommended > 0 ? '+' : '−'}$${digitFormater(
                        Math.abs(vsRecommended),
                        false,
                        1
                      )}`}
              </StyledStatValue>
            </StyledStatCopy>
          </StyledStat>
        </StyledStatGrid>
        <StyledList>
          <StyledListHead $columns={WAGE_COLUMNS}>
            <span>Player</span>
            <span>Skill / Pot</span>
            <span>Daily</span>
          </StyledListHead>
          {wagePlayers.length === 0 ? (
            <StyledEmpty>No roster wages to show</StyledEmpty>
          ) : (
            wagePlayers.map((player) => (
              <StyledListRow key={player._id} $columns={WAGE_COLUMNS}>
                <StyledPlayerName>{player.displayName}</StyledPlayerName>
                <StyledMeta>{player.skillPot}</StyledMeta>
                <StyledWage>
                  ${digitFormater(player.dailyWage, false, 1)}
                </StyledWage>
              </StyledListRow>
            ))
          )}
        </StyledList>
      </StyledSection>

      <StyledSection>
        <h3>Squad contracts</h3>
        <StyledStatGrid>
          <StyledStat>
            <StyledStatIcon $tone={expiringCount > 0 ? 'danger' : undefined}>
              <CalendarIcon />
            </StyledStatIcon>
            <StyledStatCopy>
              <StyledStatLabel>Expiring this season</StyledStatLabel>
              <StyledStatValue $tone={expiringCount > 0 ? 'danger' : undefined}>
                {expiringCount}
              </StyledStatValue>
              <StyledSquadLink to="/team/squad">View squad</StyledSquadLink>
            </StyledStatCopy>
          </StyledStat>
          <StyledStat>
            <StyledStatIcon
              $tone={
                contractsLoading && !goalTracked
                  ? undefined
                  : unmetGoalCount > 0
                    ? 'danger'
                    : 'success'
              }
            >
              <GoalIcon />
            </StyledStatIcon>
            <StyledStatCopy>
              <StyledStatLabel>Playtime goals</StyledStatLabel>
              <StyledStatValue
                $tone={
                  contractsLoading && !goalTracked
                    ? undefined
                    : unmetGoalCount > 0
                      ? 'danger'
                      : 'success'
                }
              >
                {goalTracked > 0
                  ? `${unmetGoalCount} of ${goalTracked}`
                  : contractsLoading
                    ? '…'
                    : '—'}
              </StyledStatValue>
              <StyledStatHint>
                {goalTracked > 0
                  ? "haven't met this season"
                  : contractsLoading
                    ? 'Loading playtime from player contracts'
                    : 'No playtime terms on this roster'}
              </StyledStatHint>
            </StyledStatCopy>
          </StyledStat>
        </StyledStatGrid>

        <StyledList>
          <StyledListHead $columns={EXPIRING_COLUMNS}>
            <span>Expiring</span>
            <span />
          </StyledListHead>
          {expiringPlayers.length === 0 ? (
            <StyledEmpty>No contracts ending this season</StyledEmpty>
          ) : (
            expiringPlayers.map((player) => (
              <StyledListRow key={player._id} $columns={EXPIRING_COLUMNS}>
                <StyledPlayerName>
                  {player.displayName}
                  {player.inAcademy ? <StyledMeta> · Academy</StyledMeta> : null}
                </StyledPlayerName>
                {player.isExtended ? (
                  <StyledStatus>Extended</StyledStatus>
                ) : (
                  <StyledExtendButton
                    primary
                    type="button"
                    onClick={() => openExtend(player._id)}
                  >
                    Extend
                  </StyledExtendButton>
                )}
              </StyledListRow>
            ))
          )}
        </StyledList>

        <StyledList>
          <StyledListHead $columns={PLAYTIME_COLUMNS}>
            <span>Playtime</span>
            <span>Played / goal</span>
          </StyledListHead>
          {playtimePlayers.length === 0 ? (
            <StyledEmpty>
              {contractsLoading
                ? 'Loading playtime…'
                : 'No playtime terms on this roster'}
            </StyledEmpty>
          ) : (
            playtimePlayers.map((player) => {
              const { played, playTime, unmet } = player.goal;
              const playedLabel = played == null ? '—' : played;
              const goalLabel = playTime == null ? '—' : playTime;
              return (
                <StyledListRow key={player._id} $columns={PLAYTIME_COLUMNS}>
                  <StyledPlayerName>{player.displayName}</StyledPlayerName>
                  <StyledPlaytime $tone={unmet ? 'danger' : 'success'}>
                    {playedLabel} / {goalLabel}
                  </StyledPlaytime>
                </StyledListRow>
              );
            })
          )}
        </StyledList>
      </StyledSection>
      </StyledColumns>
      </StyledPage>
    </FallbackContentAnimation>
  );
}
